import type { StoryObj } from '@storybook/react';
import React, { type JSX } from 'react';

import {
  FlexibleContentProps,
  NavigationProps,
  SacFilterProps,
} from '@sola/schema/lib/schema.ts';
import { SearchResultProps } from '@sola/skippy-search-sdk/lib/schema.ts';

import {
  FSmeta,
  FSmeta2Col,
  FSmetaDark,
  FSmetaFullBleed,
  FSmetaFullBleedDark,
  FSmetaFullBleedLight,
  FSmetaLight,
  mockContentComplex,
  mockContentModerate,
  mockContentSimple,
  mockMediaImage,
  mockMediaImage2,
  mockMediaImage3,
  mockMediaVideo,
} from '../mocks/mocks.tsx';
import { FlexibleSection } from '../organisms/flexible-section/flexible-section.tsx';

const placeholderFills = {
  default: {
    title: 'Placeholder',
    icon: 'alert-circle',
  },
  stat: {
    title: '##',
  },
  link: {
    title: 'Placeholder',
    primaryCTA: {
      title: 'Placeholder Link',
      link: '#',
    },
  },
};

export const getPlaceholderItems = (
  items: FlexibleContentProps[] | undefined,
  minLength: number,
  type?: 'default' | 'stat' | 'link',
) => {
  if (!items || items.length < minLength) {
    return [
      ...(items || []),
      ...Array(minLength - (items?.length || 0)).fill({
        body: <p>at least {minLength} items required</p>,
        ...placeholderFills[type || 'default'],
      }),
    ];
  }
  return items;
};

export const createStory = (
  Component:
    | React.ComponentType<FlexibleContentProps>
    | React.ComponentType<NavigationProps>
    | React.ComponentType<SacFilterProps>
    | React.ComponentType<SearchResultProps>,
  theme?: string,
  sectionMeta?: typeof FSmeta,
  argsAdditional?:
    | Partial<FlexibleContentProps>
    | Partial<NavigationProps>
    | Partial<SacFilterProps>
    | Partial<SearchResultProps>,
  variant?: 'default' | 'main' | 'sidebar' | 'evensplit',
  HeaderComponent?: React.ComponentType<FlexibleContentProps>,
): StoryObj => ({
  render: (args: FlexibleContentProps) => {
    const StoryComponent = Component;
    const StoryHeaderComponent = HeaderComponent;
    if (sectionMeta) {
      if (variant === 'main') {
        return (
          <FlexibleSection
            meta={sectionMeta}
            children1={<StoryComponent {...args} />}
            children2={
              <div style={{ border: '5px dashed gray', padding: '1rem' }}>
                [sidebar content here]
              </div>
            }
          />
        );
      }
      if (variant === 'sidebar') {
        return (
          <FlexibleSection
            meta={sectionMeta}
            children1={
              <div style={{ border: '5px dashed gray', padding: '1rem' }}>
                [main content here]
              </div>
            }
            children2={<StoryComponent {...args} />}
          />
        );
      }
      if (variant === 'evensplit') {
        return (
          <FlexibleSection
            meta={sectionMeta}
            children1={<StoryComponent {...args} />}
            children2={<StoryComponent {...args} />}
          />
        );
      }
      return (
        <FlexibleSection
          meta={sectionMeta}
          children1={
            <>
              {StoryHeaderComponent && (
                <StoryHeaderComponent {...mockContentSimple} />
              )}
              <StoryComponent {...args} />
            </>
          }
        />
      );
    }

    const content = (
      <div className={theme ? `${theme}-theme` : ''} style={{ width: '100%' }}>
        <StoryComponent {...args} />
      </div>
    ) as JSX.Element;
    return content;
  },
  args: argsAdditional,
});

export const createCommonStories = (
  component:
    | React.ComponentType<FlexibleContentProps>
    | React.ComponentType<NavigationProps>
    | React.ComponentType<SacFilterProps>
    | React.ComponentType<SearchResultProps>,
  argsOverride?:
    | Partial<FlexibleContentProps>
    | Partial<NavigationProps>
    | Partial<SacFilterProps>
    | Partial<SearchResultProps>,
): Record<string, StoryObj> => {
  return {
    Default: createStory(component, undefined, FSmeta, argsOverride),
    Alt: createStory(component, undefined, FSmeta, {
      ...argsOverride,
      alt: true,
    }),
    NonAlt: createStory(component, undefined, FSmeta, {
      ...argsOverride,
      alt: false,
    }),
  };
};

export const createContentStories = (
  component:
    | React.ComponentType<FlexibleContentProps>
    | React.ComponentType<NavigationProps>
    | React.ComponentType<SacFilterProps>
    | React.ComponentType<SearchResultProps>,
  argsOverride?:
    | Partial<FlexibleContentProps>
    | Partial<NavigationProps>
    | Partial<SacFilterProps>
    | Partial<SearchResultProps>,
): Record<string, StoryObj> => {
  return {
    NoImage: createStory(component, undefined, FSmeta, {
      ...argsOverride,
      media: undefined,
    }),
    NoLinks: createStory(component, undefined, FSmeta, {
      ...argsOverride,
      primaryCTA: undefined,
      secondaryCTA: undefined,
    }),
    NoImageLinks: createStory(component, undefined, FSmeta, {
      ...argsOverride,
      media: undefined,
      primaryCTA: undefined,
      secondaryCTA: undefined,
    }),
    NoImageAlt: createStory(component, undefined, FSmeta, {
      ...argsOverride,
      media: undefined,
      alt: true,
    }),
    NoLinksAlt: createStory(component, undefined, FSmeta, {
      ...argsOverride,
      primaryCTA: undefined,
      secondaryCTA: undefined,
      alt: true,
    }),
    NoImageLinksAlt: createStory(component, undefined, FSmeta, {
      ...argsOverride,
      media: undefined,
      primaryCTA: undefined,
      secondaryCTA: undefined,
      alt: true,
    }),
    HasImage: createStory(component, undefined, FSmeta, {
      ...argsOverride,
      media: mockMediaImage,
    }),
    HasVideo: createStory(component, undefined, FSmeta, {
      ...argsOverride,
      media: mockMediaVideo,
    }),
    HasVideoCTAs: createStory(component, undefined, FSmeta, {
      ...mockContentSimple,
      primaryCTA: {
        title: 'Button CTA',
        link: 'https://player.vimeo.com/video/1097918846',
      },
      secondaryCTA: {
        title: 'Button CTA',
        link: 'https://player.vimeo.com/video/1089128209?h=8bceb0e65d',
      },
      tertiaryCTA: {
        title: 'Button CTA',
        link: 'https://www.youtube.com/watch?v=CV5zOrpitNA',
      },
    }),
    Minimal: createStory(component, undefined, FSmeta, {
      ...mockContentSimple,
      ...argsOverride,
    }),
    MinimalItems: createStory(component, undefined, FSmeta, {
      ...mockContentSimple,
      ...argsOverride,
      items: [mockContentSimple, mockContentSimple, mockContentSimple],
    }),
    MinimalNoItems: createStory(component, undefined, FSmeta, {
      ...mockContentSimple,
      ...argsOverride,
      items: undefined,
    }),
    Moderate: createStory(component, undefined, FSmeta, mockContentModerate),
    ModerateItems: createStory(component, undefined, FSmeta, {
      ...mockContentModerate,
      ...argsOverride,
      items: [mockContentModerate, mockContentModerate, mockContentModerate],
    }),
    ModerateNoItems: createStory(component, undefined, FSmeta, {
      ...mockContentModerate,
      ...argsOverride,
      items: undefined,
    }),
    Complex: createStory(component, undefined, FSmeta, mockContentComplex),
    ComplexItems: createStory(component, undefined, FSmeta, {
      ...mockContentComplex,
      ...argsOverride,
      items: [
        { ...mockContentComplex, media: mockMediaImage },
        { ...mockContentComplex, media: mockMediaImage2 },
        { ...mockContentComplex, media: mockMediaImage3 },
      ],
    }),
    ComplexNoItems: createStory(component, undefined, FSmeta, {
      ...mockContentComplex,
      ...argsOverride,
      items: undefined,
    }),
    MixedItems: createStory(component, undefined, FSmeta, {
      ...mockContentComplex,
      ...argsOverride,
      items: [
        { ...mockContentSimple, media: mockMediaImage },
        { ...mockContentModerate, media: mockMediaImage2 },
        { ...mockContentComplex, media: mockMediaImage3 },
      ],
    }),
  };
};

// Section Behaviors
export const createSectionStories = (
  component:
    | React.ComponentType<FlexibleContentProps>
    | React.ComponentType<NavigationProps>
    | React.ComponentType<SacFilterProps>
    | React.ComponentType<SearchResultProps>,
  argsOverride?:
    | Partial<FlexibleContentProps>
    | Partial<NavigationProps>
    | Partial<SacFilterProps>
    | Partial<SearchResultProps>,
): Record<string, StoryObj> => {
  return {
    Default: createStory(component, undefined, FSmeta, { ...argsOverride }),
    Light: createStory(component, undefined, FSmetaLight, { ...argsOverride }),
    Dark: createStory(component, undefined, FSmetaDark, { ...argsOverride }),
    LightAlt: createStory(component, undefined, FSmetaLight, {
      alt: true,
      ...argsOverride,
    }),
    DarkAlt: createStory(component, undefined, FSmetaDark, {
      alt: true,
      ...argsOverride,
    }),
    FullBleed: createStory(component, undefined, FSmetaFullBleed, {
      ...argsOverride,
    }),
    FullBleedAlt: createStory(component, undefined, FSmetaFullBleed, {
      alt: true,
      ...argsOverride,
    }),
    FullBleedLight: createStory(component, undefined, FSmetaFullBleedLight, {
      ...argsOverride,
    }),
    FullBleedDark: createStory(component, undefined, FSmetaFullBleedDark, {
      ...argsOverride,
    }),
    Main2Col: createStory(
      component,
      undefined,
      FSmeta2Col,
      { ...argsOverride },
      'main',
    ),
    Sidebar2Col: createStory(
      component,
      undefined,
      FSmeta2Col,
      { ...argsOverride },
      'sidebar',
    ),
    Even2Col: createStory(
      component,
      undefined,
      FSmeta,
      { ...argsOverride },
      'evensplit',
    ),
    FullWidthEven2Col: createStory(
      component,
      undefined,
      FSmetaFullBleedLight,
      { ...argsOverride },
      'main',
    ),
  };
};
